import axios from "axios";
import { useState, useEffect, MouseEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthModal from '../Modal/AuthenticateModal';
import pulpliveworldlogo from "../assets/img/Logo_Pulp_Full_Black.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser, faCalendarDay, faTicket, faUsersBetweenLines, faTag, faShoppingCart, faNewspaper, faVideo, faImages, faToolbox, faDoorOpen } from '@fortawesome/free-solid-svg-icons';
import { toast } from "sonner";

export default function Sidebar() {
	const [author, setAuthor] = useState<string | null>(null);
	const [role, setRole] = useState<string | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false); 
	const [isLoading, setIsLoading] = useState(false);

	const openAuthModal = () => {
		setIsModalOpen(true); 
	};
	
	const closeAuthModal = () => {
		setIsModalOpen(false); 
	};

	const userLogout: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
		e.preventDefault(); 
		setIsLoading(true);

		try {
			await axios.post(
				"https://cdn.pulp.ph/api/logout",
				{}, 
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("authToken")}`,
					},
				}
			);

			localStorage.removeItem("authToken");
			localStorage.removeItem("author");
			localStorage.removeItem("role");

			delete axios.defaults.headers.common["Authorization"];

			toast.success(<p className="text-[16px]">You have been logged out successfully.</p>, {
				duration: 1000,
			});

			setTimeout(() => {
				window.location.href = "/login";
			}, 1100);
		} catch (error) {
			alert("Unauthenticated. Please log in.");
			window.location.href = "/login";
		}
	};

	useEffect(() => {
		const storedAuthor = localStorage.getItem("author");
		const storedRole = localStorage.getItem("role");

		if (storedAuthor) {
			setAuthor(storedAuthor);
		}

		if (storedRole) {
			setRole(storedRole);
		}
	}, []);

	const navigate = useNavigate();
	const location = useLocation();
	const isActive = (path: string) => location.pathname === path;

	return (
		<>
			<AuthModal
                isOpen={isModalOpen}
                onClose={closeAuthModal}
                title="Action Verification"
                content={<div className='text-base mb-3'>Are you sure you want to log out?</div>}
                footer={
                    <>
                        <button 
                            className="m-0 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                            onClick={closeAuthModal}>
                            Close
                        </button>
                        <button 
							disabled={isLoading}
							className="m-0 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
                            onClick={userLogout}>
							{isLoading ? "Confirming..." : "Confirm"}
                        </button>
                    </>
                }
            />

			<aside className="col-start-1 row-start-1 bg-white">
				<div className="px-0">
					<div className="pulp-logo h-auto w-auto flex justify-center items-center py-2">
						<img src={pulpliveworldlogo} className="h-auto w-[150px]" alt="pulp logo" />
					</div>
					
					<div className="userProfile container flex items-center gap-3 py-0 px-3">
						<div className="userPicture flex items-center justify-center">
							<FontAwesomeIcon icon={faCircleUser} style={{ fontSize: '32px' }} className="text-[#1f2328]" />
						</div>

						<div className="userDescription flex flex-col items-start">
							<h2 className="GraphikSemibold text-[14px] text-[#1f2328] font-semibold pt-2">{author}</h2> 
							<p className="GraphikRegular text-[14px] text-[#1f2328] font-normal">{role}</p>
						</div>
					</div>
				</div>

				<nav className="mt-1">
					<ul className="GraphikSemibold text-[#1f2328]">
						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/view/post/Show") ? "text-red-500 font-bold" : ""}`}>
							<a href="/view/post/Show" 
								aria-current={isActive("/view/post/Show") ? "page" : undefined} 
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faCalendarDay} className="mr-2" />
								Shows
							</a>
						</li>

						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/view/post/ticket") ? "text-red-500 font-bold" : ""}`}>
							<a href="/view/post/ticket"
								aria-current={isActive("/view/post/ticket") ? "page" : undefined}
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faTicket} className="mr-2" />
								Ticket
							</a>
						</li>

						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/view/post/seatPlan") ? "text-red-500 font-bold" : ""}`}>
							<a href="/view/post/seat-plan"
								aria-current={isActive("/view/post/seat-plan") ? "page" : undefined}
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faUsersBetweenLines} className="mr-2" />
								Seat Plan
							</a>
						</li>

						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/view/post/ticket-prices") ? "text-red-500 font-bold" : ""}`}>
							<a href="/view/post/ticket-prices"
								aria-current={isActive("/view/post/ticket-prices") ? "page" : undefined}
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faTag} className="mr-2" />
								Ticket Price
							</a>
						</li>

						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/view/post/buy-links") ? "text-red-500 font-bold" : ""}`}>
							<a href="/view/post/buy-links"
								aria-current={isActive("/view/post/buy-links") ? "page" : undefined}
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faShoppingCart} className="mr-2" />
								Buy Links
							</a>
						</li>

						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/view/post/news-media") ? "text-red-500 font-bold" : ""}`}>
							<a href="/view/post/news-media"
								aria-current={isActive("/view/post/news-media") ? "page" : undefined}
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faNewspaper} className="mr-2" />
								News & Media
							</a>
						</li>

						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/view/post/video") ? "text-red-500 font-bold" : ""}`}>
							<a href="/view/post/video"
								aria-current={isActive("/view/post/video") ? "page" : undefined}
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faVideo} className="mr-2" />
								Video
							</a>
						</li>

						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/view/post/gallery") ? "text-red-500 font-bold" : ""}`}>
							<a href="/view/post/gallery"
								aria-current={isActive("/view/post/gallery") ? "page" : undefined}
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faImages} className="mr-2" />
								Gallery
							</a>
						</li>

						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/admin") ? "text-red-500 font-bold" : ""}`}>
							{/* <a href="/admin" 
								aria-current={isActive('/admin') ? 'page' : undefined} 
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faToolbox} className="mr-2" />
								Admin
							</a> */}
							<p className="flex items-center text-[16px] font-bold py-3 px-3" data-tooltip-id="feature">
								<FontAwesomeIcon icon={faToolbox} className="mr-2" />
								Admin
							</p>
						</li>

						{/* <li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40 ${isActive("/view/post/menu") ? "text-red-500 font-bold" : ""}`}>
							<a href="/view/post/menu" 
								aria-current={isActive("/view/post/menu") ? "page" : undefined} 
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faCalendarDay} className="mr-2" />
								Menu
							</a>
						</li> */}

						<li className={`hover:text-red-500 hover:bg-gray-300 hover:bg-opacity-40`}>
							<button
								onClick={openAuthModal}
								className="flex items-center text-[16px] font-bold py-3 px-3">
								<FontAwesomeIcon icon={faDoorOpen} className="mr-2" />
								Logout
							</button>
						</li>
					</ul>
				</nav>
			</aside>
		</>
	);
}
